/* UNIQQ Gallery — storefront behaviour. No dependencies. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const routes = theme.routes;
  const strings = theme.strings;
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  /* ------------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------------ */

  const debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const escapeHTML = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const fetchSection = async (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    return parseHTML(await response.text());
  };

  // Scroll lock shared by every overlay (menus, drawers, modals).
  let lockCount = 0;
  const lockScroll = () => {
    lockCount += 1;
    if (lockCount === 1) {
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = scrollbar > 0 ? `${scrollbar}px` : '';
      document.body.classList.add('is-locked');
    }
  };
  const unlockScroll = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('is-locked');
      document.body.style.paddingRight = '';
    }
  };

  // Long side in cm of a size value such as "Large - 45x60 cm / 18x24″ | Popular".
  const sizeLongSide = (value) => {
    const match = String(value).match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*cm/i);
    if (!match) return null;
    return Math.max(parseFloat(match[1].replace(',', '.')), parseFloat(match[2].replace(',', '.')));
  };

  // Same key as snippets/size-key.liquid: "Large - 45x60 cm / 18x24″ | Popular" -> "45x60" (short side first).
  const sizeKey = (value) => {
    let main = String(value).split('|')[0].trim();
    if (main.includes(' - ')) main = main.split(' - ').pop().trim();
    const sides = main.split('/')[0].toLowerCase().replaceAll('cm', '').trim().split('x');
    if (sides.length !== 2) return null;
    const a = parseFloat(sides[0]);
    const b = parseFloat(sides[1]);
    if (!(a > 0 && b > 0)) return null;
    return a <= b ? `${a}x${b}` : `${b}x${a}`;
  };

  // Same match as snippets/size-variant-map.liquid: the key itself, else the nearest key within 12%
  // on each side (A4 21x29.7 <-> 20x30, A2 42x59.4 <-> 40x60…), else null.
  const nearestSizeKey = (key, keys) => {
    if (!key) return null;
    if (keys.includes(key)) return key;
    const [short, long] = key.split('x').map(parseFloat);
    let best = null;
    let bestDiff = 1;
    keys.forEach((candidate) => {
      const [a, b] = candidate.split('x').map(parseFloat);
      const diff = Math.max(Math.abs(a - short) / short, Math.abs(b - long) / long);
      if (diff <= 0.12 && diff < bestDiff) {
        best = candidate;
        bestDiff = diff;
      }
    });
    return best;
  };

  // Same words as snippets/mount-bleed.liquid: the full-bleed mount value, translations included.
  const BLEED_WORDS = ['bleed', 'full', 'pleine', 'fond perdu', 'randlos', 'anschnitt', 'sin borde', 'sangrado', 'senza bordo', 'piena', 'uten kant', 'blødning'];
  const isBleed = (value) => BLEED_WORDS.some((word) => String(value).toLowerCase().includes(word));

  // Same formula as snippets/size-scale.liquid: 0.42 for the smallest size, 1 for the largest.
  const sizeScale = (value, values) => {
    const sides = values.map(sizeLongSide).filter((side) => side != null);
    const current = sizeLongSide(value);
    if (current == null || sides.length < 2) return 1;
    const min = Math.min(...sides);
    const max = Math.max(...sides);
    if (max === min) return 1;
    return Math.round((0.42 + (0.58 * (current - min)) / (max - min)) * 1000) / 1000;
  };

  /* ------------------------------------------------------------------------
     Header: scroll states, drawer menu, mobile menu
     ------------------------------------------------------------------------ */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.menuDrawer = this.querySelector('[data-menu-drawer]');
      this.mobileMenu = this.querySelector('[data-mobile-menu]');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      this.querySelectorAll('[data-menu-open]').forEach((button) =>
        button.addEventListener('click', () => (this.menuDrawer?.classList.contains('is-open') ? this.closeMenu() : this.openMenu()))
      );
      this.menuDrawer?.querySelectorAll('[data-menu-close]').forEach((el) => el.addEventListener('click', () => this.closeMenu()));
      this.setupMenuDrawer();

      this.querySelector('[data-mobile-menu-toggle]')?.addEventListener('click', () => this.toggleMobileMenu());
      this.mobileMenu?.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-mobile-submenu-open]');
        const closer = event.target.closest('[data-mobile-submenu-close]');
        if (opener) {
          opener.closest('.mobile-menu__item').classList.add('is-open');
          opener.setAttribute('aria-expanded', 'true');
        } else if (closer) {
          const item = closer.closest('.mobile-menu__item');
          item.classList.remove('is-open');
          item.querySelector('[data-mobile-submenu-open]')?.setAttribute('aria-expanded', 'false');
        }
      });

      DESKTOP.addEventListener('change', () => {
        if (DESKTOP.matches) this.closeMobileMenu();
        else this.closeMenu();
      });
    }

    onScroll() {
      const y = window.scrollY;
      const scrolled = y > 10;
      this.classList.toggle('is-scrolled', scrolled);
      document.body.classList.toggle('is-scrolled', scrolled);
      if (Math.abs(y - this.lastY) < 6) return;
      const goingDown = y > this.lastY && y > this.offsetHeight + 40;
      if (!document.body.classList.contains('menu-open') && !document.body.classList.contains('mobile-menu-open')) {
        this.classList.toggle('is-hidden', goingDown);
      }
      this.lastY = y;
    }

    // First opening: put the menu in the layout, then let one frame pass so it can transition in.
    wake(element) {
      if (!element?.classList.contains('is-dormant')) return;
      element.classList.remove('is-dormant');
      void element.offsetWidth;
    }

    openMenu() {
      if (!this.menuDrawer || this.menuDrawer.classList.contains('is-open')) return;
      window.closeOverlays?.('menu');
      this.wake(this.menuDrawer);
      this.menuDrawer.classList.add('is-open');
      this.menuDrawer.setAttribute('aria-hidden', 'false');
      this.querySelectorAll('[data-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('menu-open');
      const firstPreview = this.menuDrawer.querySelector('.menu-drawer__links > .menu-drawer__item:has(> [data-menu-preview])');
      this.setPreview(firstPreview);
      lockScroll();
    }

    closeMenu() {
      if (!this.menuDrawer?.classList.contains('is-open')) return;
      this.menuDrawer.classList.remove('is-open', 'has-open-panel');
      this.menuDrawer.setAttribute('aria-hidden', 'true');
      this.menuDrawer.querySelectorAll('.is-open, .is-previewed').forEach((el) => el.classList.remove('is-open', 'is-previewed'));
      this.menuDrawer.querySelectorAll('[data-menu-parent]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
      this.querySelectorAll('[data-menu-open]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('menu-open');
      unlockScroll();
    }

    setPreview(item) {
      if (!item) return;
      const list = item.parentElement;
      list.querySelectorAll(':scope > .is-previewed').forEach((el) => el.classList.remove('is-previewed'));
      item.classList.add('is-previewed');
    }

    openPanel(item) {
      this.menuDrawer.querySelectorAll('.menu-drawer__item.is-open').forEach((el) => {
        if (el !== item) {
          el.classList.remove('is-open');
          el.querySelector('[data-menu-parent]')?.setAttribute('aria-expanded', 'false');
        }
      });
      item.classList.add('is-open');
      item.querySelector('[data-menu-parent]')?.setAttribute('aria-expanded', 'true');
      this.menuDrawer.classList.add('has-open-panel');
      const firstSub = item.querySelector('.menu-panel__item:has(> [data-menu-preview])');
      this.setPreview(firstSub);
    }

    closePanels() {
      this.menuDrawer.querySelectorAll('.menu-drawer__item.is-open').forEach((el) => {
        el.classList.remove('is-open');
        el.querySelector('[data-menu-parent]')?.setAttribute('aria-expanded', 'false');
      });
      this.menuDrawer.classList.remove('has-open-panel');
    }

    setupMenuDrawer() {
      if (!this.menuDrawer) return;
      this.menuDrawer.querySelectorAll('.menu-drawer__links > .menu-drawer__item').forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (item.classList.contains('has-children')) {
            this.openPanel(item);
          } else {
            this.closePanels();
            if (item.querySelector(':scope > [data-menu-preview]')) this.setPreview(item);
          }
        });
        item.querySelector('[data-menu-parent]')?.addEventListener('click', () => {
          if (item.classList.contains('is-open')) this.closePanels();
          else this.openPanel(item);
        });
      });
      this.menuDrawer.querySelectorAll('.menu-panel__item').forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (item.querySelector(':scope > [data-menu-preview]')) this.setPreview(item);
        });
      });
    }

    toggleMobileMenu() {
      if (document.body.classList.contains('mobile-menu-open')) this.closeMobileMenu();
      else this.openMobileMenu();
    }

    openMobileMenu() {
      window.closeOverlays?.('mobile-menu');
      this.wake(this.mobileMenu);
      document.body.classList.add('mobile-menu-open');
      this.mobileMenu?.setAttribute('aria-hidden', 'false');
      this.querySelector('[data-mobile-menu-toggle]')?.setAttribute('aria-expanded', 'true');
      this.classList.remove('is-hidden');
      lockScroll();
    }

    closeMobileMenu() {
      if (!document.body.classList.contains('mobile-menu-open')) return;
      document.body.classList.remove('mobile-menu-open');
      this.mobileMenu?.setAttribute('aria-hidden', 'true');
      this.mobileMenu?.querySelectorAll('.mobile-menu__item.is-open').forEach((el) => el.classList.remove('is-open'));
      this.querySelector('[data-mobile-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      unlockScroll();
    }
  }
  customElements.define('site-header', SiteHeader);

  /* ------------------------------------------------------------------------
     Drawers
     ------------------------------------------------------------------------ */

  class SideDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (this.isOpen) return;
      window.closeOverlays?.(this.id);
      this.trigger = trigger;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.loadSections();
      setTimeout(() => this.querySelector('.drawer__panel')?.focus({ preventScroll: true }), 60);
    }

    // Content kept out of the page and rendered on first opening (e.g. the 200+ countries).
    loadSections() {
      this.querySelectorAll('[data-section-load]:not([data-loaded])').forEach(async (target) => {
        target.dataset.loaded = 'true';
        try {
          const doc = await fetchSection(routes.root || '/', target.dataset.sectionLoad);
          target.innerHTML = doc.querySelector('[data-section-content]')?.innerHTML ?? '';
        } catch (error) {
          delete target.dataset.loaded;
          console.error(error);
        }
      });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      this.trigger?.focus?.({ preventScroll: true });
    }
  }
  customElements.define('side-drawer', SideDrawer);

  class CartDrawer extends SideDrawer {
    connectedCallback() {
      super.connectedCallback();
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-quantity-step]');
        const remove = event.target.closest('[data-cart-remove]');
        if (step) {
          const line = step.closest('[data-cart-line]');
          const input = line.querySelector('[data-quantity-input]');
          const quantity = Math.max(0, parseInt(input.value || '0', 10) + parseInt(step.dataset.quantityStep, 10));
          input.value = quantity;
          Cart.change(line, quantity);
        } else if (remove) {
          Cart.change(remove.closest('[data-cart-line]'), 0);
        }
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (input) Cart.change(input.closest('[data-cart-line]'), Math.max(0, parseInt(input.value || '0', 10)));
      });
    }

    renderSection(html) {
      const doc = parseHTML(html);
      const fresh = doc.querySelector('[data-cart-drawer-content]');
      const current = this.querySelector('[data-cart-drawer-content]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      Wishlist.sync(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  const Cart = {
    sectionId: 'cart-drawer',

    drawer() {
      return document.getElementById('CartDrawer');
    },

    async updateCount() {
      try {
        const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (error) {
        console.error(error);
      }
    },

    async add(formData) {
      formData.append('sections', this.sectionId);
      formData.append('sections_url', window.location.pathname);
      const response = await fetch(`${routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || strings.cartError);
      }
      if (data.sections?.[this.sectionId]) this.drawer()?.renderSection(data.sections[this.sectionId]);
      await this.updateCount();
      this.drawer()?.open();
      return data;
    },

    async change(lineEl, quantity) {
      if (!lineEl) return;
      lineEl.classList.add('is-loading');
      const onCartPage = Boolean(lineEl.closest('[data-cart-page]'));
      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: parseInt(lineEl.dataset.cartLine, 10),
            quantity,
            sections: this.sectionId,
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || strings.cartError);
        if (onCartPage) {
          window.location.reload();
          return;
        }
        if (data.sections?.[this.sectionId]) this.drawer()?.renderSection(data.sections[this.sectionId]);
        await this.updateCount();
      } catch (error) {
        console.error(error);
        lineEl.classList.remove('is-loading');
        window.alert(error.message || strings.cartError);
      }
    },
  };

  // Cart page: same line controls as the drawer, then reload.
  class CartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-quantity-step]');
        const remove = event.target.closest('[data-cart-remove]');
        if (step) {
          const line = step.closest('[data-cart-line]');
          const input = line.querySelector('[data-quantity-input]');
          Cart.change(line, Math.max(0, parseInt(input.value || '0', 10) + parseInt(step.dataset.quantityStep, 10)));
        } else if (remove) {
          Cart.change(remove.closest('[data-cart-line]'), 0);
        }
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (input) Cart.change(input.closest('[data-cart-line]'), Math.max(0, parseInt(input.value || '0', 10)));
      });
    }
  }
  customElements.define('cart-page', CartPage);

  /* ------------------------------------------------------------------------
     Search panel
     ------------------------------------------------------------------------ */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.querySelectorAll('[data-search-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.input?.addEventListener('input', debounce(() => this.search(), 250));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      window.closeOverlays?.('search');
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('search-open');
      lockScroll();
      setTimeout(() => this.input?.focus(), 80);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('search-open');
      unlockScroll();
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      this.results.classList.add('is-loading');
      const url = new URL(routes.predictiveSearch, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,query');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][prefix]', 'last');
      url.searchParams.set('section_id', 'predictive-search');
      try {
        const html = await fetch(url.toString(), { signal: this.controller.signal }).then((r) => r.text());
        const fresh = parseHTML(html).querySelector('[data-predictive-results]');
        this.results.innerHTML = fresh ? fresh.outerHTML : '';
        Wishlist.sync(this.results);
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      } finally {
        this.results.classList.remove('is-loading');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------------------------
     Wishlist (saved in the browser)
     ------------------------------------------------------------------------ */

  const Wishlist = {
    key: 'uniqq-wishlist',

    items() {
      try {
        return JSON.parse(window.localStorage.getItem(this.key)) || [];
      } catch (error) {
        return [];
      }
    },

    save(items) {
      try {
        window.localStorage.setItem(this.key, JSON.stringify(items));
      } catch (error) {
        /* storage unavailable: the list lives for this page only */
      }
      this.memory = items;
      this.sync();
    },

    current() {
      return this.memory || this.items();
    },

    has(handle) {
      return this.current().some((item) => item.handle === handle);
    },

    toggle(button) {
      let data;
      try {
        data = JSON.parse(button.dataset.wishlistData);
      } catch (error) {
        return;
      }
      const items = this.current().filter((item) => item.handle !== data.handle);
      if (items.length === this.current().length) items.unshift(data);
      this.save(items);
    },

    remove(handle) {
      this.save(this.current().filter((item) => item.handle !== handle));
    },

    sync(root = document) {
      const items = this.current();
      root.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        const saved = items.some((item) => item.handle === button.dataset.wishlistItem);
        button.setAttribute('aria-pressed', String(saved));
        button.setAttribute('aria-label', saved ? strings.removeFromWishlist : strings.addToWishlist);
      });
      document.querySelectorAll('.header__wishlist').forEach((el) => el.classList.toggle('has-items', items.length > 0));
      const count = document.querySelector('[data-wishlist-count]');
      if (count) count.textContent = items.length || '';
      const list = document.querySelector('[data-wishlist-list]');
      if (list) {
        list.innerHTML = items
          .map(
            (item) => `
          <li class="wishlist-item">
            <a href="${escapeHTML(item.url)}" class="wishlist-item__media">${item.image ? `<img src="${escapeHTML(item.image)}" alt="" loading="lazy">` : ''}</a>
            <div class="wishlist-item__info">
              <div>
                <a href="${escapeHTML(item.url)}" class="wishlist-item__title">${escapeHTML(item.title)}</a>
                ${item.artist ? `<p class="wishlist-item__meta">${escapeHTML(item.artist)}</p>` : ''}
                <p class="wishlist-item__meta">${escapeHTML(item.price)}</p>
              </div>
              <button type="button" class="cart-item__remove" data-wishlist-remove="${escapeHTML(item.handle)}">${escapeHTML(strings.wishlistRemove)}</button>
            </div>
          </li>`
          )
          .join('');
      }
    },
  };

  /* ------------------------------------------------------------------------
     View in room
     ------------------------------------------------------------------------ */

  // Copy of an artwork for a room: images load now, at room size.
  const roomCopy = (source) => {
    const copy = source.cloneNode(true);
    copy.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      img.sizes = '40vw';
    });
    return copy;
  };

  // Hangs an artwork in a room box: portrait artworks in the lounge photo, others above the sofa.
  const hangInRoom = (room, source) => {
    const slot = room?.querySelector('[data-room-art]');
    if (!slot || !source) return;
    const portrait = source.classList.contains('artwork--portrait');
    room.classList.toggle('room--portrait', portrait);
    room.classList.toggle('room--landscape', !portrait);
    slot.replaceChildren(roomCopy(source));
  };

  class RoomViewer extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-room-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    // Hangs a copy of the artwork at true scale: portrait artworks in the lounge photo,
    // landscape and square ones above the sofa (see snippets/room-scene).
    open(source) {
      if (!source) return;
      window.closeOverlays?.('room');
      if (this.classList.contains('is-dormant')) {
        this.classList.remove('is-dormant');
        void this.offsetWidth;
      }
      hangInRoom(this.querySelector('[data-room]'), source);
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      setTimeout(() => this.querySelector('.room-modal__dialog')?.focus({ preventScroll: true }), 60);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
    }
  }
  customElements.define('room-viewer', RoomViewer);

  /* ------------------------------------------------------------------------
     Product page
     ------------------------------------------------------------------------ */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.counter = this.querySelector('[data-gallery-counter]');
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.index() - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.index() + 1));
      this.track?.addEventListener('scroll', debounce(() => this.updateCounter(), 60), { passive: true });
    }

    index() {
      if (!this.track) return 0;
      return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
    }

    go(index) {
      const count = this.slides.length;
      const target = (index + count) % count;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
    }

    updateCounter() {
      if (this.counter) this.counter.textContent = `${this.index() + 1} / ${this.slides.length}`;
    }
  }
  customElements.define('product-gallery', ProductGallery);

  class ProductPage extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-product-json]').textContent);
      } catch (error) {
        return;
      }
      this.form = this.querySelector('[data-product-form]');
      this.art = this.querySelector('[data-pdp-art]');
      this.gallery = this.querySelector('[data-gallery]');
      this.idInput = this.querySelector('[data-variant-id]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.error = this.querySelector('[data-form-error]');
      this.priceEl = this.querySelector('[data-price]');
      this.priceLabel = this.querySelector('[data-price-label]');
      this.comparePrice = this.querySelector('[data-compare-price]');
      this.fromPrice = this.priceEl?.textContent.trim();
      this.fieldsets = [...this.querySelectorAll('[data-option]')];
      // The page opens with a default variant selected; the room keeps its own default until the
      // visitor picks something (or arrives with ?variant=).
      this.userSelected = new URLSearchParams(window.location.search).has('variant');

      const sizeFieldset = this.fieldsets.find((fieldset) => fieldset.dataset.optionRole === 'size');
      this.sizeValues = sizeFieldset ? [...sizeFieldset.querySelectorAll('input')].map((input) => input.value) : [];

      this.form?.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"][name^="option-"]')) this.onOptionChange(event.target);
      });
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart();
      });

      this.update(false);
      this.setupSticky();
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);
    }

    findVariant(options) {
      if (options.some((option) => option == null)) return null;
      return this.data.variants.find((variant) => variant.options.every((option, index) => option === options[index])) || null;
    }

    onOptionChange(input) {
      this.userSelected = true;
      const fieldset = input.closest('[data-option]');
      const role = fieldset.dataset.optionRole;
      const label = fieldset.querySelector('[data-option-selected]');
      if (label) {
        label.textContent = role === 'size' ? input.nextElementSibling.querySelector('.size-row__name')?.textContent ?? input.value : input.value;
      }
      if (role === 'size') {
        fieldset.querySelector('[data-option-required]')?.setAttribute('hidden', '');
        this.swapArtwork(input.value);
        this.updateMaterialLinks(input.value);
      }
      this.applyFinish();
      this.update(true);
    }

    // The room shows the visitor's selection once it is a full variant, else the default of
    // snippets/room-artwork (Vintage Border, else Classic Bleed, else Vintage Bleed; largest, black).
    roomSource() {
      if (this.userSelected && this.variant) return this.art?.querySelector('[data-artwork]');
      return this.querySelector('template[data-room-default]')?.content.firstElementChild;
    }

    // Material and finish links open the other product in the same size, or the nearest one.
    updateMaterialLinks(sizeValue) {
      const key = sizeKey(sizeValue);
      this.querySelectorAll('[data-size-variants]').forEach((link) => {
        let map;
        try {
          map = JSON.parse(link.dataset.sizeVariants);
        } catch (error) {
          return;
        }
        const url = new URL(link.getAttribute('href'), window.location.origin);
        url.searchParams.delete('variant');
        const match = nearestSizeKey(key, Object.keys(map));
        if (match) url.searchParams.set('variant', map[match]);
        link.setAttribute('href', url.pathname + url.search);
      });
    }

        // The gallery draws the size chosen: its own crop, at its own scale.
    swapArtwork(sizeValue) {
      if (!this.art) return;
      const template = [...this.gallery.querySelectorAll('template[data-art-size]')].find((tpl) => tpl.dataset.artSize === sizeValue);
      const fresh = template?.content.firstElementChild;
      const current = this.art.querySelector('[data-artwork]');
      if (fresh && current) {
        const next = fresh.cloneNode(true);
        const img = next.querySelector('img');
        if (img) img.loading = 'eager';
        current.replaceWith(next);
      }
      this.art.style.setProperty('--size-scale', sizeScale(sizeValue, this.sizeValues));
      this.gallery.querySelector('[data-gallery-track]')?.scrollTo({ left: 0, behavior: 'smooth' });
    }

    applyFinish() {
      const artwork = this.art?.querySelector('[data-artwork]');
      if (!artwork) return;
      this.fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        if (!checked) return;
        if (fieldset.dataset.optionRole === 'frame' && checked.dataset.frameToken) {
          [...artwork.classList].filter((cls) => cls.startsWith('artwork--frame-')).forEach((cls) => artwork.classList.remove(cls));
          artwork.classList.add(`artwork--frame-${checked.dataset.frameToken}`);
        }
        if (fieldset.dataset.optionRole === 'mount') {
          artwork.classList.toggle('artwork--mount', !isBleed(checked.value));
        }
        // Canvas thickness: depth of the 3D canvas's edge.
        if (checked.dataset.depthCm) artwork.style.setProperty('--depth-cm', checked.dataset.depthCm);
      });
    }

    update(pushState) {
      const options = this.selectedOptions();
      const variant = this.findVariant(options);
      const complete = options.every((option) => option != null);
      this.variant = variant;

      if (this.idInput && this.fieldsets.length) this.idInput.value = variant ? variant.id : '';

      if (this.priceEl) {
        this.priceEl.textContent = variant ? variant.price_formatted : this.fromPrice;
        this.priceLabel?.toggleAttribute('hidden', Boolean(variant));
        if (this.comparePrice) {
          const onSale = variant && variant.compare_at_price > variant.price;
          this.comparePrice.hidden = !onSale;
          if (onSale) this.comparePrice.textContent = variant.compare_formatted;
        }
      }

      if (this.addButton && this.fieldsets.length) {
        this.addButton.disabled = !variant || !variant.available;
        if (variant && !variant.available) this.addButton.textContent = strings.soldOut;
        else if (complete && !variant) this.addButton.textContent = strings.unavailable;
        else this.addButton.textContent = strings.addToCart;
      }

      // Price of each size with the other options as selected.
      const sizeFieldset = this.fieldsets.find((fieldset) => fieldset.dataset.optionRole === 'size');
      if (sizeFieldset) {
        const sizeIndex = this.fieldsets.indexOf(sizeFieldset);
        sizeFieldset.querySelectorAll('input').forEach((input) => {
          const candidate = options.map((option, index) => (index === sizeIndex ? input.value : option));
          let match = this.findVariant(candidate);
          if (!match) match = this.data.variants.find((v) => v.options[sizeIndex] === input.value);
          const priceEl = input.nextElementSibling?.querySelector('[data-value-price]');
          if (priceEl && match) priceEl.textContent = match.price_formatted;
        });
      }

      if (pushState) hangInRoom(document.querySelector('[data-pdp-room] [data-room]'), this.roomSource());

      if (pushState && variant) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    async addToCart() {
      if (!this.form || !this.idInput?.value) return;
      const formData = new FormData(this.form);
      [...formData.keys()].filter((key) => key.startsWith('option-')).forEach((key) => formData.delete(key));
      this.addButton?.classList.add('is-loading');
      if (this.error) this.error.hidden = true;
      try {
        await Cart.add(formData);
      } catch (error) {
        if (this.error) {
          this.error.textContent = error.message;
          this.error.hidden = false;
        }
      } finally {
        this.addButton?.classList.remove('is-loading');
      }
    }

    setupSticky() {
      const sticky = this.querySelector('[data-pdp-sticky]');
      const anchor = this.form;
      if (!sticky || !anchor || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          sticky.classList.toggle('is-visible', passed);
          sticky.setAttribute('aria-hidden', String(!passed));
          sticky.querySelector('button')?.setAttribute('tabindex', passed ? '0' : '-1');
        },
        { threshold: 0 }
      );
      observer.observe(anchor);
      sticky.querySelector('[data-pdp-sticky-button]')?.addEventListener('click', () => {
        const top = this.querySelector('.pdp__info-inner')?.getBoundingClientRect().top ?? 0;
        window.scrollTo({ top: window.scrollY + top - 100, behavior: 'smooth' });
      });
    }
  }
  customElements.define('product-page', ProductPage);

  // Colour of a frame token, oak or not: "white-oak" -> "white".
  const frameColour = (token) => String(token || '').replace(/-oak$/, '');

  // Product page of a V4 unique product (sections/main-product-unique): the finishes are the
  // values of option 1, chosen on the page. A finish change hides the sizes and frames the finish
  // does not have, keeps the size (else the nearest, else the largest) and the frame colour
  // (else black, else the first), and swaps the finish's texts. The mount is not a variant: its
  // buttons, its line item properties and the mat follow the finish's setting (uniqq.mount).
  class UniqueProductPage extends ProductPage {
    connectedCallback() {
      this.finishFieldset = this.querySelector('[data-option-role="finish"]');
      this.mountFieldset = this.querySelector('[data-mount-option]');
      this.mountProperty = this.querySelector('[data-mount-property]');
      this.mountCode = this.querySelector('[data-mount-code]');
      this.mountChosen = null;
      super.connectedCallback();
      if (!this.data?.finishes) return;

      this.querySelectorAll('button[data-material]').forEach((button) => button.addEventListener('click', () => this.selectMaterial(button)));
      this.artKey = this.currentArtKey();

      // A shared link may name the mount (?mount=border), when the finish lets the visitor choose.
      const mount = new URLSearchParams(window.location.search).get('mount');
      if ((mount === 'border' || mount === 'bleed') && this.currentFinish()?.mount?.offered) {
        this.mountChosen = mount;
        this.syncMount();
        this.applyFinish();
      }
    }

    finishKey() {
      return this.finishFieldset?.querySelector('input:checked')?.dataset.finish ?? null;
    }

    currentFinish() {
      return this.data.finishes?.[this.finishKey()] ?? null;
    }

    finishVariants() {
      const key = this.finishKey();
      return this.data.variants.filter((variant) => variant.finish === key);
    }

    fieldsetOf(role) {
      return this.fieldsets.find((fieldset) => fieldset.dataset.optionRole === role);
    }

    onOptionChange(input) {
      this.userSelected = true;
      if (input.name === 'option-mount') {
        this.mountChosen = input.value;
        this.syncMount();
        this.applyFinish();
        this.update(true);
        return;
      }
      if (input.closest('[data-option]')?.dataset.optionRole === 'finish') {
        this.changeFinish();
        return;
      }
      super.onOptionChange(input);
    }

    // A material selects its main finish (Classic, Canvas, Metal, Poster, else its first).
    selectMaterial(button) {
      if (button.getAttribute('aria-current') === 'true') return;
      const input = this.finishFieldset?.querySelector(`input[data-finish="${button.dataset.materialFinish}"]`);
      if (!input) return;
      input.checked = true;
      this.onOptionChange(input);
    }

    changeFinish() {
      const finish = this.currentFinish();
      if (!finish) return;
      const variants = this.finishVariants();

      // Sizes: the finish's own; the same size, else the nearest within 12%, else the largest.
      const sizeFieldset = this.fieldsetOf('size');
      if (sizeFieldset) {
        const sizes = [...new Set(variants.map((variant) => variant.options[1]))];
        const inputs = [...sizeFieldset.querySelectorAll('input')];
        const previous = sizeFieldset.querySelector('input:checked')?.value;
        let size = sizes.includes(previous) ? previous : null;
        if (!size) {
          const match = nearestSizeKey(sizeKey(previous), sizes.map(sizeKey));
          size = match ? sizes.find((value) => sizeKey(value) === match) : [...sizes].sort((a, b) => sizeLongSide(b) - sizeLongSide(a))[0];
        }
        inputs.forEach((input) => {
          input.closest('li').hidden = !sizes.includes(input.value);
          input.checked = input.value === size;
        });
        const checked = inputs.find((input) => input.checked);
        const label = sizeFieldset.querySelector('[data-option-selected]');
        if (label && checked) label.textContent = checked.nextElementSibling?.querySelector('.size-row__name')?.textContent ?? checked.value;
      }

      // Frames: swatches, Slim / Thick for the canvas, nothing for the metal and the poster.
      const frameFieldset = this.fieldsetOf('frame');
      if (frameFieldset) {
        const key = this.finishKey();
        let kind = 'color';
        if (key === 'canvas-classic') kind = 'thickness';
        else if (['metal', 'metal-brushed', 'poster'].includes(key)) kind = 'none';
        const frames = new Set(variants.map((variant) => variant.options[2]));
        const inputs = [...frameFieldset.querySelectorAll('input')];
        const previous = frameFieldset.querySelector('input:checked');
        const candidates = inputs.filter((input) => frames.has(input.value));
        let next = candidates.find((input) => input.value === previous?.value);
        if (!next) {
          const colour = frameColour(previous?.dataset.frameToken);
          next =
            candidates.find((input) => colour && frameColour(input.dataset.frameToken) === colour) ||
            candidates.find((input) => frameColour(input.dataset.frameToken) === 'black') ||
            candidates[0];
        }
        inputs.forEach((input) => {
          input.closest('li').hidden = !frames.has(input.value);
          input.checked = input === next;
        });
        frameFieldset.hidden = kind === 'none';
        frameFieldset.querySelectorAll('[data-frame-kind]').forEach((list) => {
          list.hidden = kind === 'none' || list.dataset.frameKind !== kind;
        });
        const name = frameFieldset.querySelector('[data-frame-label]');
        if (name) name.textContent = kind === 'thickness' ? this.data.labels.thickness : this.data.labels.frame;
        const label = frameFieldset.querySelector('[data-option-selected]');
        if (label && next) label.textContent = next.value;
      }

      this.updatePicker(finish);
      this.updateTexts(finish);
      this.syncMount();
      this.swapArtwork();
      this.applyFinish();
      this.update(true);
    }

    // Material buttons, finishes of the material, heading and description of the finish.
    updatePicker(finish) {
      const picker = this.finishFieldset;
      if (!picker) return;
      picker.querySelectorAll('button[data-material]').forEach((button) => {
        if (button.dataset.material === finish.material) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const items = [...picker.querySelectorAll('li[data-material]')];
      items.forEach((item) => {
        item.hidden = item.dataset.material !== finish.material;
      });
      // The finish's one name is its option value, as in the title, the cart and the checkout.
      const heading = picker.querySelector('[data-material-selected]');
      if (heading) heading.textContent = finish.value;
      const description = picker.querySelector('[data-finish-description]');
      if (description) {
        description.textContent = finish.description;
        description.hidden = !finish.description;
      }
    }

    // Finish after the title, and the feature lines that follow the finish.
    updateTexts(finish) {
      const title = this.querySelector('[data-finish-title]');
      if (title) title.textContent = `· ${finish.value}`;
      this.querySelectorAll('[data-feature]').forEach((item) => {
        const field = item.dataset.feature;
        if (!['print', 'frame', 'sizes'].includes(field)) return;
        const text = finish[field] || '';
        const target = item.querySelector('[data-feature-text]');
        if (target) target.textContent = text;
        item.hidden = !text;
      });
    }

    // Mount of the finish: the visitor's choice when the finish offers one (kept from finish to
    // finish), else the finish's value; null without a mount (canvas, metal).
    mountValue() {
      const mount = this.currentFinish()?.mount;
      if (!mount) return null;
      if (mount.offered && this.mountChosen) return this.mountChosen;
      return mount.value;
    }

    syncMount() {
      const mount = this.currentFinish()?.mount;
      const value = this.mountValue();
      if (this.mountFieldset) {
        this.mountFieldset.hidden = !mount?.offered;
        this.mountFieldset.querySelectorAll('input').forEach((input) => {
          input.checked = input.value === value;
        });
        const label = this.mountFieldset.querySelector('[data-mount-selected]');
        if (label && value) label.textContent = this.data.labels[value];
      }
      [this.mountProperty, this.mountCode].forEach((field) => {
        if (field) field.disabled = !value;
      });
      if (value) {
        if (this.mountProperty) this.mountProperty.value = this.data.labels[value];
        if (this.mountCode) this.mountCode.value = value;
      }
    }

    // Mat of the mount, and the thicker frame of the Vintage and Museum (the Classic, Vintage and
    // Museum share their gallery template).
    applyFinish() {
      super.applyFinish();
      const artwork = this.art?.querySelector('[data-artwork]');
      if (!artwork) return;
      artwork.classList.toggle('artwork--mount', this.mountValue() === 'border');
      artwork.classList.toggle('artwork--thick-frame', Boolean(this.currentFinish()?.thick_frame));
    }

    // Template of the gallery for the choice: its drawing and its ratio family.
    currentArtKey() {
      const finish = this.currentFinish();
      const size = this.fieldsetOf('size')?.querySelector('input:checked')?.value;
      const variant = this.finishVariants().find((candidate) => candidate.options[1] === size);
      return finish && variant ? `${finish.drawing}|${variant.family}` : null;
    }

    // The gallery draws the choice: the template of its drawing and family when it changes,
    // then its size among the finish's sizes (applyFinish sets frame, mat and depth).
    swapArtwork() {
      if (!this.art) return;
      const key = this.currentArtKey();
      const current = this.art.querySelector('[data-artwork]');
      if (key && key !== this.artKey && current) {
        const template = [...this.gallery.querySelectorAll('template[data-art]')].find((tpl) => tpl.dataset.art === key);
        const fresh = template?.content.firstElementChild;
        if (fresh) {
          const next = fresh.cloneNode(true);
          next.querySelectorAll('img').forEach((img) => {
            img.loading = 'eager';
          });
          current.replaceWith(next);
          this.artKey = key;
        }
      }
      const size = this.fieldsetOf('size')?.querySelector('input:checked')?.value;
      const sizes = [...new Set(this.finishVariants().map((variant) => variant.options[1]))];
      const long = sizeLongSide(size);
      if (long) this.art.querySelector('[data-artwork]')?.style.setProperty('--long-cm', long);
      this.art.style.setProperty('--size-scale', sizeScale(size, sizes));
      this.gallery.querySelector('[data-gallery-track]')?.scrollTo({ left: 0, behavior: 'smooth' });
    }

    // ?variant= as on every product page, plus ?mount= when the visitor's mount is not the
    // finish's first value.
    update(pushState) {
      super.update(pushState);
      if (!pushState || !this.data?.finishes) return;
      const url = new URL(window.location.href);
      const mount = this.currentFinish()?.mount;
      const value = this.mountValue();
      if (mount?.offered && value && value !== mount.value) url.searchParams.set('mount', value);
      else url.searchParams.delete('mount');
      window.history.replaceState({}, '', url.toString());
    }
  }
  customElements.define('unique-product-page', UniqueProductPage);

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const html = await fetch(this.dataset.url).then((r) => r.text());
          const fresh = parseHTML(html).querySelector('product-recommendations');
          if (fresh?.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            Wishlist.sync(this);
          }
        } catch (error) {
          console.error(error);
        }
      };
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ------------------------------------------------------------------------
     Collection page: view, filters, load more
     ------------------------------------------------------------------------ */

  class CollectionPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.viewKey = 'uniqq-grid-view';

      this.restoreView();
      this.querySelectorAll('[data-grid-view]').forEach((input) =>
        input.addEventListener('change', () => {
          this.setView(input.value);
          try {
            window.localStorage.setItem(this.viewKey, input.value);
          } catch (error) {
            /* ignore */
          }
        })
      );

      this.form?.addEventListener('change', debounce(() => this.render(this.formUrl()), 350));
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.render(this.formUrl());
      });

      this.addEventListener('click', (event) => {
        const facetLink = event.target.closest('[data-facet-link]');
        const loadMore = event.target.closest('[data-load-more]');
        const loadAll = event.target.closest('[data-load-all]');
        if (facetLink) {
          event.preventDefault();
          this.render(facetLink.href);
        } else if (loadMore) {
          event.preventDefault();
          this.loadMore();
        } else if (loadAll) {
          event.preventDefault();
          this.loadAll();
        }
      });

      window.addEventListener('popstate', () => this.render(window.location.href, false));
    }

    restoreView() {
      let saved = null;
      try {
        saved = window.localStorage.getItem(this.viewKey);
      } catch (error) {
        /* ignore */
      }
      const input = saved && this.querySelector(`[data-grid-view][value="${saved}"]`);
      if (input && input.closest('label').offsetParent !== null) {
        input.checked = true;
        this.setView(saved);
      }
    }

    setView(view) {
      this.querySelectorAll('[data-product-grid]').forEach((grid) => (grid.dataset.view = view));
    }

    currentView() {
      return this.querySelector('[data-grid-view]:checked')?.value || 'default';
    }

    formUrl() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      const base = this.dataset.baseUrl || window.location.pathname;
      const query = params.toString();
      return query ? `${base}?${query}` : base;
    }

    async render(url, pushState = true) {
      const results = this.querySelector('[data-collection-results]');
      results?.classList.add('is-loading');
      try {
        const doc = await fetchSection(url, this.sectionId);
        const freshResults = doc.querySelector('[data-collection-results]');
        if (freshResults && results) results.innerHTML = freshResults.innerHTML;

        const openFacets = new Set([...this.querySelectorAll('details.facet[open]')].map((el) => el.dataset.facet));
        const freshBody = doc.querySelector('[data-facets-body]');
        const body = this.querySelector('[data-facets-body]');
        if (freshBody && body) {
          const scroll = body.scrollTop;
          body.innerHTML = freshBody.innerHTML;
          body.querySelectorAll('details.facet').forEach((el) => {
            el.open = openFacets.has(el.dataset.facet) || el.open;
          });
          body.scrollTop = scroll;
        }
        const freshFooter = doc.querySelector('.facets__footer');
        const footer = this.querySelector('.facets__footer');
        if (freshFooter && footer) footer.innerHTML = freshFooter.innerHTML;

        ['[data-collection-count]', '[data-filter-count]'].forEach((selector) => {
          const fresh = doc.querySelector(selector);
          const current = this.querySelector(selector);
          if (fresh && current) {
            current.textContent = fresh.textContent;
            current.hidden = fresh.hidden;
          }
        });

        this.setView(this.currentView());
        Wishlist.sync(this);
        if (pushState) window.history.pushState({}, '', url);
      } catch (error) {
        console.error(error);
        window.location.href = url;
      } finally {
        this.querySelector('[data-collection-results]')?.classList.remove('is-loading');
      }
    }

    async loadMore() {
      const link = this.querySelector('[data-load-more]');
      if (!link || this.loading) return false;
      this.loading = true;
      link.classList.add('is-loading');
      try {
        const doc = await fetchSection(link.href, this.sectionId);
        const grid = this.querySelector('[data-product-grid]');
        doc.querySelectorAll('[data-product-grid] > *').forEach((card) => grid?.appendChild(document.importNode(card, true)));
        const freshPagination = doc.querySelector('[data-pagination]');
        const pagination = this.querySelector('[data-pagination]');
        if (freshPagination && pagination) pagination.replaceWith(document.importNode(freshPagination, true));
        Wishlist.sync(this);
        return Boolean(this.querySelector('[data-load-more]'));
      } catch (error) {
        console.error(error);
        window.location.href = link.href;
        return false;
      } finally {
        this.loading = false;
      }
    }

    async loadAll() {
      let more = true;
      while (more) {
        more = await this.loadMore();
      }
    }
  }
  customElements.define('collection-page', CollectionPage);

  /* ------------------------------------------------------------------------
     Global wiring
     ------------------------------------------------------------------------ */

  // "View in room" on a card: the card shows the framed print, the room shows the product's room
  // artwork (sections/room-art), fetched once per product.
  const roomArtCache = new Map();
  const openCardRoom = async (card, button, viewer) => {
    const url = card.querySelector('.product-card__content')?.getAttribute('href');
    let source = url ? roomArtCache.get(url) : null;
    if (!source && url) {
      button.classList.add('is-loading');
      try {
        const doc = await fetchSection(url, 'room-art');
        source = doc.querySelector('[data-room-art-source] [data-artwork]');
        if (source) roomArtCache.set(url, source);
      } catch (error) {
        console.error(error);
      } finally {
        button.classList.remove('is-loading');
      }
    }
    viewer?.open(source || card.querySelector('[data-artwork]'));
  };

  // Close every overlay except the one being opened.
  window.closeOverlays = (except) => {
    const header = document.querySelector('site-header');
    if (except !== 'menu') header?.closeMenu();
    if (except !== 'mobile-menu') header?.closeMobileMenu();
    if (except !== 'search') document.querySelector('predictive-search')?.close();
    if (except !== 'room') document.querySelector('room-viewer')?.close();
    document.querySelectorAll('side-drawer.is-open, cart-drawer.is-open').forEach((drawer) => {
      if (drawer.id !== except) drawer.close();
    });
  };

  document.addEventListener('click', (event) => {
    const drawerOpener = event.target.closest('[data-drawer-open]');
    if (drawerOpener) {
      const drawer = document.getElementById(drawerOpener.dataset.drawerOpen);
      if (drawer?.open) {
        event.preventDefault();
        drawer.open(drawerOpener);
      }
      return;
    }

    const templateOpener = event.target.closest('[data-drawer-template]');
    if (templateOpener) {
      const template = document.getElementById(templateOpener.dataset.drawerTemplate);
      const drawer = document.getElementById('PageDrawer');
      if (template && drawer) {
        drawer.querySelector('[data-page-drawer-body]').replaceChildren(template.content.cloneNode(true));
        drawer.open(templateOpener);
      }
      return;
    }

    if (event.target.closest('[data-search-open]')) {
      document.querySelector('predictive-search')?.open();
      return;
    }

    const wishlistButton = event.target.closest('[data-wishlist-toggle]');
    if (wishlistButton) {
      event.preventDefault();
      Wishlist.toggle(wishlistButton);
      return;
    }

    const wishlistRemove = event.target.closest('[data-wishlist-remove]');
    if (wishlistRemove) {
      Wishlist.remove(wishlistRemove.dataset.wishlistRemove);
      return;
    }

    const roomButton = event.target.closest('[data-room-open]');
    if (roomButton) {
      const card = roomButton.closest('[data-product-card]');
      const viewer = document.querySelector('room-viewer');
      if (card) openCardRoom(card, roomButton, viewer);
      else viewer?.open(document.querySelector('product-page, unique-product-page')?.roomSource());
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') window.closeOverlays();
  });

  const init = () => {
    Wishlist.sync();

    // Product room label slides in once the page has settled.
    document.querySelectorAll('[data-room-label]').forEach((label) => setTimeout(() => label.classList.add('is-visible'), 400));

    // Reveal sections as they enter the viewport.
    const reveals = document.querySelectorAll('[data-reveal]');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.12 }
      );
      reveals.forEach((el) => observer.observe(el));
    } else {
      reveals.forEach((el) => el.classList.add('is-revealed'));
    }

    // Theme editor: reopen state-less sections cleanly.
    document.addEventListener('shopify:section:load', () => Wishlist.sync());
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
